import type { LayeredStorage } from "./storage";

export type TelecomTokenListener = (token: string | null) => void;

/**
 * Store for managing telecom API authentication token on the client.
 * Similar to TokenStore but stores a single token instead of a token pair.
 */
export class TelecomTokenStore {
  private token: string | null = null;
  private loadPromise: Promise<string | null> | null = null;
  private readonly listeners = new Set<TelecomTokenListener>();

  constructor(
    private readonly storage: LayeredStorage,
    private readonly storageKey: string,
  ) {}

  /**
   * Load token from storage.
   * Returns cached token if already loaded, otherwise loads from storage.
   */
  async load(): Promise<string | null> {
    if (this.token !== null) {
      return this.token;
    }

    if (!this.loadPromise) {
      this.loadPromise = (async () => {
        const storedToken = await this.storage.getItem(this.storageKey);
        
        // Handle case where token might be stored as JSON object
        if (storedToken) {
          try {
            // Try to parse as JSON in case it was stored as an object
            const parsed = JSON.parse(storedToken);
            // If it's an object with a token property, extract it
            if (typeof parsed === 'object' && parsed !== null && 'token' in parsed) {
              this.token = typeof parsed.token === 'string' ? parsed.token : null;
            } else if (typeof parsed === 'string') {
              this.token = parsed;
            } else {
              this.token = null;
            }
          } catch {
            // If parsing fails, treat it as a string token
            this.token = typeof storedToken === 'string' ? storedToken : null;
          }
        } else {
          this.token = null;
        }
        
        this.loadPromise = null;
        return this.token;
      })();
    }

    return this.loadPromise;
  }

  /**
   * Get current token from memory (without loading from storage).
   */
  getToken(): string | null {
    // Ensure token is always a string, not an object
    if (this.token === null) {
      return null;
    }
    if (typeof this.token === 'string') {
      return this.token;
    }
    // If token is an object, try to extract string value
    if (typeof this.token === 'object' && this.token !== null && 'token' in this.token) {
      const tokenValue = (this.token as { token?: unknown }).token;
      return typeof tokenValue === 'string' ? tokenValue : null;
    }
    // Fallback: convert to string
    return String(this.token);
  }

  /**
   * Set token and persist to storage.
   */
  async setToken(token: string | null): Promise<void> {
    // Ensure token is always a string, not an object
    let tokenString: string | null = null;
    
    if (token !== null) {
      if (typeof token === 'string') {
        tokenString = token;
      } else if (typeof token === 'object' && token !== null && 'token' in token) {
        // If token is an object with a token property, extract it
        const tokenValue = (token as { token?: unknown }).token;
        tokenString = typeof tokenValue === 'string' ? tokenValue : null;
      } else {
        // Fallback: convert to string
        tokenString = String(token);
      }
    }
    
    this.token = tokenString;

    if (tokenString) {
      await this.storage.setItem(this.storageKey, tokenString);
    } else {
      await this.storage.removeItem(this.storageKey);
    }

    this.notify();
  }

  /**
   * Clear token from memory and storage.
   */
  async clear(): Promise<void> {
    await this.setToken(null);
  }

  /**
   * Subscribe to token changes.
   * Returns unsubscribe function.
   */
  subscribe(listener: TelecomTokenListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify() {
    for (const listener of this.listeners) {
      listener(this.token);
    }
  }
}

